import React, { useState, useCallback } from 'react'
import { token } from '@atlaskit/tokens'
import Button from '@atlaskit/button'
import LinkIcon from '@atlaskit/icon/glyph/link'
import { JiraIssuePicker } from './JiraIssuePicker'

/**
 * BUG LINK INPUT COMPONENT
 *
 * Autocomplete input for searching and linking Jira issues to an execution.
 * Uses Jira's issue picker API to search as the user types.
 *
 * Features:
 * - Autocomplete dropdown with issue search
 * - Shows issue type icon, key, and summary in dropdown
 * - Loading state during linking
 * - Error display for failed operations
 *
 * @param {function} onLink - Callback when issue is linked (receives issueKey)
 * @param {boolean} disabled - Whether the input is disabled
 * @param {React.ReactNode} extraButton - Optional extra button to render next to Link button
 */
export function BugLinkInput({ onLink, disabled = false, extraButton = null }) {
  // State for selected issue (from dropdown)
  const [selectedIssue, setSelectedIssue] = useState(null)
  // State for loading
  const [isLinking, setIsLinking] = useState(false)
  // State for error message
  const [error, setError] = useState(null)

  /**
   * HANDLE ISSUE SELECT
   * Called when user selects an issue from the dropdown.
   */
  const handleIssueSelect = useCallback((option) => {
    setSelectedIssue(option)
    if (error) {
      setError(null)
    }
  }, [error])

  /**
   * HANDLE LINK
   * Links the selected issue.
   */
  const handleLink = useCallback(async () => {
    if (!selectedIssue) {
      setError('Please select an issue first')
      return
    }

    // Clear previous error
    setError(null)

    setIsLinking(true)
    try {
      await onLink(selectedIssue.value)
      // Clear selection on success
      setSelectedIssue(null)
    } catch (err) {
      // Extract error message
      const errorMessage = err.response?.data?.message
        || err.message
        || 'Failed to link issue. Please try again.'
      setError(errorMessage)
    } finally {
      setIsLinking(false)
    }
  }, [selectedIssue, onLink])

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Label */}
      <label
        style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: 600,
          color: token('color.text.subtle', '#5E6C84'),
          marginBottom: '6px'
        }}
      >
        Link Jira Issue
      </label>

      {/* Input row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px'
        }}
      >
        {/* Issue picker */}
        <div style={{ flex: 1 }}>
          <JiraIssuePicker
            value={selectedIssue}
            onChange={handleIssueSelect}
            placeholder="Search for a Jira issue..."
            isDisabled={disabled || isLinking}
          />
        </div>

        {/* Link button */}
        <Button
          appearance="primary"
          onClick={handleLink}
          isLoading={isLinking}
          isDisabled={disabled || !selectedIssue}
          iconBefore={!isLinking && <LinkIcon size="small" label="Link" />}
          style={{ marginTop: '2px' }}
        >
          Link
        </Button>

        {/* Extra button (e.g., +Bug) */}
        {extraButton}
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            marginTop: '6px',
            fontSize: '12px',
            color: token('color.text.danger', '#AE2E24')
          }}
        >
          {error}
        </div>
      )}

      {/* Helper text */}
      <div
        style={{
          marginTop: '6px',
          fontSize: '11px',
          color: token('color.text.subtlest', '#7A869A')
        }}
      >
        Search by issue key or summary, then click Link
      </div>
    </div>
  )
}

export default BugLinkInput
